import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDashboardData } from './hooks/useDashboardData.ts';
import TimeSeriesChart from './components/TimeSeriesChart.tsx';
import StrategyCard from './components/StrategyCard.tsx';
import { format, parseISO } from 'date-fns';
import GradientText from '../../components/common/GradientText.tsx';

const timeRanges = ['7d', '30d', '90d'];

const HolographicContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px rgba(138, 43, 226, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  }
}));

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    return format(date, 'PPpp');
  } catch (error) {
    console.error('Invalid date:', dateString);
    return 'N/A';
  }
};

const Dashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const { 
    metrics, 
    strategies, 
    lastAnalysisDate,
    nextUpdateScheduled,
    modelAccuracy,
    isLoading, 
    error 
  } = useDashboardData();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#8a2be2' }} />
      </Box>
    );
  }

  if (error || !metrics) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error loading dashboard data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <GradientText variant="h4" gradient="primary">
          Performance Dashboard
        </GradientText>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(_, value) => value && setTimeRange(value)}
          sx={{
            '& .MuiToggleButton-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              borderColor: 'rgba(138, 43, 226, 0.2)',
              '&.Mui-selected': {
                color: '#8a2be2',
                backgroundColor: 'rgba(138, 43, 226, 0.1)',
              },
              '&:hover': {
                borderColor: 'rgba(0, 255, 255, 0.3)',
                backgroundColor: 'rgba(0, 255, 255, 0.1)',
              },
            },
          }}
        >
          {timeRanges.map((range) => (
            <ToggleButton key={range} value={range}>
              {range}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {/* Performance Metrics Chart */}
        <Grid item xs={12}>
          <HolographicContainer sx={{ p: 3 }}>
            <TimeSeriesChart
              data={metrics.performanceHistory}
              forecast={metrics.forecast}
              confidenceIntervals={metrics.confidenceIntervals}
              title="Performance Metrics"
            />
          </HolographicContainer>
        </Grid>

        {/* Efficiency and Accuracy Trends */}
        <Grid item xs={12} md={6}>
          <HolographicContainer sx={{ p: 3 }}>
            <TimeSeriesChart
              data={metrics.efficiencyTrend}
              title="Efficiency Trend"
              height={300}
            />
          </HolographicContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <HolographicContainer sx={{ p: 3 }}>
            <TimeSeriesChart
              data={metrics.accuracyTrend}
              title="Accuracy Trend"
              height={300}
            />
          </HolographicContainer>
        </Grid>

        {/* Strategy Cards */}
        <Grid item xs={12}>
          <GradientText variant="h5" sx={{ mb: 2 }} gradient="secondary">
            Active Strategies
          </GradientText>
          <Grid container spacing={3}>
            {strategies?.map((strategy) => (
              <Grid item xs={12} md={4} key={strategy.id}>
                <StrategyCard strategy={strategy} />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Model Info */}
        <Grid item xs={12}>
          <HolographicContainer sx={{ p: 3, mt: 3 }}>
            <GradientText variant="body3" gradient="accent">
              Last Analysis: {formatDate(lastAnalysisDate)}
            </GradientText>
            <GradientText variant="body2" gradient="accent">
              Next Update: {formatDate(nextUpdateScheduled)}
            </GradientText>
            <GradientText variant="body2" gradient="accent">
              Model Accuracy: {modelAccuracy ?? 'N/A'}%
            </GradientText>
          </HolographicContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 